/**
 * Model Intelligence — guard + bits presentacionales compartidos
 * (Sprint 285 Stream G; REQ-MINT-012/013; SEC-022).
 *
 * El gating es **fail-closed**: sin `Module:model-intel#view` (o con la
 * comprobación cargando/error/sin tenant) la sección se oculta y una URL
 * directa resuelve a una página de denegación representativa sin consultar
 * datos (patrón `FrontierGuard`). El precio no añade gating UI: el backend
 * omite las tarifas sin permiso y la UI pinta «sin dato».
 */

import type { ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useI18n } from '@/i18n';

export function ModelIntelGuard({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const canView = useModuleAccess('model-intel', 'view');

  if (!canView) {
    return (
      <div className="p-6" data-testid="model-intel-denied">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" aria-hidden="true" />
              {t('modelIntel.denied.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('modelIntel.denied.message')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  return <>{children}</>;
}

/** Nota de procedencia obligatoria por superficie (ADR-123 §3). */
export function ProvenanceNote() {
  const { t } = useI18n();
  return (
    <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="provenance-note">
      {t('modelIntel.governance.sourceNote')}
    </p>
  );
}
